import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Award, User, Brain } from 'lucide-react';

/**
 * Leaderboard Component
 * Displays a ranked list of top performers
 */
const Leaderboard = ({ 
  entries = [], 
  loading = false, 
  currentUserId = null,
  compact = false,
  title = 'Top Scholars'
}) => {
  // No entries state
  if (!loading && entries.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 text-center">
        <Trophy className="mx-auto text-gray-300 mb-3" size={32} />
        <h3 className="text-gray-700 font-medium">No Scores Yet</h3>
        <p className="text-sm text-gray-500 mt-1">
          Be the first to complete the challenge!
        </p>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-3"></div>
        <p className="text-gray-600 text-sm">Loading leaderboard...</p>
      </div>
    );
  }
  
  // Get appropriate medal for rank
  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="text-yellow-500" size={20} />;
      case 2:
        return <Medal className="text-gray-400" size={20} />;
      case 3:
        return <Medal className="text-amber-700" size={20} />;
      default:
        return <Award className="text-green-500" size={20} />;
    }
  };
  
  // Get background style for rank
  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-yellow-50 border-yellow-200';
      case 2:
        return 'bg-gray-50 border-gray-200';
      case 3:
        return 'bg-amber-50 border-amber-200';
      default:
        return 'bg-white border-gray-100';
    }
  };
  
  // Compact view for homepages and widgets
  if (compact) {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-3">
          <h2 className="font-bold text-base flex items-center">
            <Trophy className="mr-2" size={18} />
            {title}
          </h2>
        </div>
        
        <div className="divide-y divide-gray-100">
          {entries.slice(0, 5).map((entry) => (
            <motion.div
              key={entry.userId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: entry.rank * 0.05 }}
              className={`flex items-center p-3 ${
                entry.userId === currentUserId ? 'bg-green-50' : ''
              }`}
            >
              <div className="w-7 h-7 flex items-center justify-center mr-3">
                {getMedalIcon(entry.rank)}
              </div>
              
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 mr-3">
                {entry.avatar ? (
                  <img
                    src={entry.avatar}
                    alt={entry.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                    <User size={16} />
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">
                  {entry.name || 'Anonymous'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {entry.university || 'University Student'}
                </div>
              </div>
              
              <div className="text-sm font-bold text-teal-600">
                {entry.totalScore}
                <span className="text-xs ml-1 font-normal">pts</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }
  
  // Full leaderboard view
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4">
        <h2 className="font-bold text-lg flex items-center">
          <Trophy className="mr-2" size={22} />
          {title}
        </h2>
      </div>
      
      <div className="p-4">
        {/* Top 3 Podium */}
        <div className="flex items-end justify-center mb-8 mt-4">
          {entries.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="w-24 mx-2"
            >
              <div className="text-center mb-2">
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto border-2 border-gray-300">
                  {entries[1].avatar ? (
                    <img
                      src={entries[1].avatar}
                      alt={entries[1].name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-600">
                      <User size={24} />
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {entries[1].name}
                  </div>
                  <div className="text-xs text-gray-500">{entries[1].totalScore} pts</div>
                </div>
              </div>
              <div className="h-16 bg-gray-200 rounded-t-lg relative flex items-end justify-center">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Medal size={24} className="text-gray-400" />
                </div>
                <div className="text-gray-700 font-bold pb-1">2</div>
              </div>
            </motion.div>
          )}
          
          {entries.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="w-28 mx-2 z-10"
            >
              <div className="text-center mb-2">
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto border-4 border-yellow-400 shadow-lg">
                  {entries[0].avatar ? (
                    <img
                      src={entries[0].avatar}
                      alt={entries[0].name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                      <User size={32} />
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {entries[0].name}
                  </div>
                  <div className="text-xs font-bold text-yellow-600">{entries[0].totalScore} pts</div>
                </div>
              </div>
              <div className="h-24 bg-yellow-400 rounded-t-lg relative flex items-end justify-center">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Trophy size={28} className="text-yellow-600" />
                </div>
                <div className="text-yellow-800 font-bold pb-1">1</div>
              </div>
            </motion.div>
          )}
          
          {entries.length > 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="w-24 mx-2"
            >
              <div className="text-center mb-2">
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto border-2 border-amber-700">
                  {entries[2].avatar ? (
                    <img
                      src={entries[2].avatar}
                      alt={entries[2].name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-amber-100 flex items-center justify-center text-amber-700">
                      <User size={24} />
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {entries[2].name}
                  </div>
                  <div className="text-xs text-gray-500">{entries[2].totalScore} pts</div>
                </div>
              </div>
              <div className="h-12 bg-amber-700 rounded-t-lg relative flex items-end justify-center">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Medal size={24} className="text-amber-600" />
                </div>
                <div className="text-amber-100 font-bold pb-1">3</div>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* Remaining Leaderboard Entries */}
        <div className="mt-6 space-y-2">
          {entries.slice(3).map((entry) => (
            <motion.div
              key={entry.userId}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: entry.rank * 0.05 }}
              className={`flex items-center p-3 rounded-lg border ${
                getRankStyle(entry.rank)
              } ${
                entry.userId === currentUserId ? 'ring-2 ring-green-300' : ''
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 mr-3 font-bold text-gray-700">
                {entry.rank}
              </div>
              
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 mr-3">
                {entry.avatar ? (
                  <img
                    src={entry.avatar}
                    alt={entry.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                    <User size={18} />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="font-medium text-gray-800">
                  {entry.name}
                  {entry.userId === currentUserId && (
                    <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      You
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {entry.university || 'University Student'}
                </div>
              </div>
              
              <div className="text-right">
                <div className="font-bold text-teal-600">
                  {entry.totalScore}
                  <span className="text-xs ml-1 font-normal">pts</span>
                </div>
                <div className="text-xs text-gray-500">
                  {entry.quizCount || 0} {entry.quizCount === 1 ? 'quiz' : 'quizzes'}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;